import * as React from 'react'
import { useMemo } from 'react'

import {
  CheckIcon,
  ChevronDown,
  ChevronRight,
  CopyCheckIcon,
} from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  CardList,
  ICardListGroupConfig,
  useCardListCore,
} from '~/client/src/shared/components/CardList'
import {
  GROUP_INDENT_STEP_PX,
  GroupConnectorLines,
  GroupCount,
  GroupIndent,
} from '~/client/src/shared/components/GroupHeader'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import WarningNote from '~/client/src/shared/components/WarningNote/WarningNote'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'

import { buildRecordTree, IRecordTree } from '../buildRecordTree'
import { filterSelectable } from '../filterSelectable'
import { useResourcePickerContext } from '../ResourcePickerContext'
import { useFirstInitiallySelectedId } from '../useFirstInitiallySelectedId'
import { RESOURCE_ICON_CLASS_NAME } from '../useResourceRenderItem'
import { PickerCountRow } from './PickerCountRow'
import { PickerLoading } from './PickerLoading'

// Figma "LBS List Item": a 16px inset, then the checkbox and its 8px gap in multi mode.
// Each level's line runs under the parent's 16px chevron, through its centre.
const ROW_INSET_PX = 16
const CHECKBOX_SLOT_PX = 24
const CHEVRON_CENTRE_PX = 8
const CHEVRON_SLOT_PX = 24
const ICON_SLOT_PX = 24

interface ITreeRow {
  id: string
  record: IResourceRecord
  depth: number
  hasChildren: boolean
  isExpanded: boolean
  count?: number
}

interface IRowProps {
  row: ITreeRow
  tree: IRecordTree
  isSearching: boolean
  onToggleExpanded: (id: string) => void
}

const PickerTreeRow: React.FC<IRowProps> = React.memo(
  ({ row, tree, isSearching, onToggleExpanded }) => {
    const {
      selectedIds,
      mode,
      pickItem,
      toggleGroup,
      isItemDisabled,
      getNote,
      getRecordTypeId,
      getTypeIcon,
    } = useResourcePickerContext()
    const { id, record, depth, hasChildren, isExpanded, count } = row
    const isMulti = mode === 'multi'
    const resourceTypeId = getRecordTypeId(record)
    const typeIcon = getTypeIcon(resourceTypeId)
    // An ancestor that is not offered is only there to place what is under it.
    const isOffered = tree.offeredIds.has(id)
    const isSelected = selectedIds.has(id)
    const isDisabled = isOffered && !isSelected && !!isItemDisabled?.(id)
    const isSelectable = isOffered && !isDisabled
    const canToggle = hasChildren && !isSearching
    const note = isOffered ? getNote?.(id) : null
    const checkboxSlotPx = isMulti ? CHECKBOX_SLOT_PX : 0

    const nested = useMemo(
      () =>
        isMulti && hasChildren
          ? filterSelectable(
              tree.getOfferedSubtree(id),
              selectedIds,
              isItemDisabled,
            )
          : [],
      [isMulti, hasChildren, tree, id, selectedIds, isItemDisabled],
    )
    const hasNested = nested.some(item => item.id !== id)
    const nestedLabel = nested.every(item => selectedIds.has(item.id))
      ? Localization.translator.deselectNestedItems
      : Localization.translator.selectNestedItems

    const activate = () => {
      if (isSelectable) {
        pickItem(id)
      } else if (canToggle) {
        onToggleExpanded(id)
      }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Keys on "Select nested items" stay with it.
      if (event.target !== event.currentTarget) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        activate()
      } else if (
        canToggle &&
        event.key === (isExpanded ? 'ArrowLeft' : 'ArrowRight')
      ) {
        event.preventDefault()
        onToggleExpanded(id)
      }
    }

    return (
      <div className="tw:relative">
        <GroupConnectorLines
          depth={depth}
          leftOffsetPx={ROW_INSET_PX + checkboxSlotPx + CHEVRON_CENTRE_PX}
        />
        <div
          role="treeitem"
          // Its name only: the count and "Select nested items" are not part of it.
          aria-label={record.name}
          aria-level={depth + 1}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={isOffered ? isSelected : undefined}
          aria-disabled={(!isSelectable && !canToggle) || undefined}
          tabIndex={0}
          onClick={activate}
          onKeyDown={handleKeyDown}
          // What is said under the row is part of it: it highlights and picks with it.
          className={classList({
            'tw:group/row tw:flex tw:flex-col tw:outline-none tw:transition-colors tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring': true,
            // Hovering the chevron highlights only the chevron.
            'tw:cursor-pointer tw:hover:not-has-[[data-tree-chevron]:hover]:bg-accent-soft':
              isSelectable || canToggle,
            'tw:bg-accent-soft': isSelected,
          })}
        >
          <div
            className={classList({
              'tw:flex tw:h-8 tw:items-center tw:gap-2 tw:pl-4': true,
              // The 28px "Select nested items" button sits in a 12px inset, the check in 16px.
              'tw:pr-3': isMulti,
              'tw:pr-4': !isMulti,
            })}
          >
            {isMulti && (
              <Checkbox
                checked={isSelected}
                disabled={!isSelectable}
                displayOnly={true}
                aria-hidden={true}
                tabIndex={-1}
              />
            )}
            {/* No gap: a level is the parent's chevron and its margin, as on a band. */}
            <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center">
              <GroupIndent depth={depth} />
              {hasChildren ? (
                // Mouse only, and never picks the row: the row is the tree item, and its
                // arrow keys open and close it. The 24px ::after is the hit area around
                // the 16px chevron, so a near miss does not pick the row either.
                <span
                  aria-hidden="true"
                  data-tree-chevron={true}
                  onClick={event => {
                    event.stopPropagation()
                    if (canToggle) onToggleExpanded(id)
                  }}
                  className={classList({
                    'tw:relative tw:mr-2 tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-xs tw:after:absolute tw:after:-inset-1': true,
                    'tw:cursor-pointer tw:opacity-85 tw:hover:bg-accent-soft':
                      canToggle,
                    'tw:opacity-50': !canToggle,
                  })}
                >
                  {isExpanded ? (
                    <ChevronDown className="tw:size-4" />
                  ) : (
                    <ChevronRight className="tw:size-4" />
                  )}
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className="tw:mr-2 tw:size-4 tw:shrink-0"
                />
              )}
              <span
                className={classList({
                  'tw:flex tw:min-w-0 tw:items-center tw:gap-2': true,
                  // Greyed like a restricted row: not offered, or offered but not allowed.
                  'tw:opacity-50': !isSelectable,
                })}
              >
                <span className="tw:flex tw:size-4 tw:shrink-0 tw:opacity-85">
                  {renderResourceRecordIcon(
                    resourceTypeId,
                    record,
                    typeIcon,
                    RESOURCE_ICON_CLASS_NAME,
                  )}
                </span>
                <span className="tw:truncate tw:text-app-dense-medium tw:text-foreground">
                  {record.name}
                </span>
                {count != null && <GroupCount>{count}</GroupCount>}
              </span>
            </span>
            {hasNested && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={nestedLabel}
                      onClick={event => {
                        event.stopPropagation()
                        toggleGroup(nested)
                      }}
                      className="tw:opacity-0 tw:group-hover/row:not-group-has-[[data-tree-chevron]:hover]/row:opacity-100 tw:focus-visible:opacity-100 tw:pointer-coarse:opacity-100"
                    >
                      <CopyCheckIcon aria-hidden="true" />
                    </Button>
                  }
                />
                <TooltipContent side="left">{nestedLabel}</TooltipContent>
              </Tooltip>
            )}
            {!isMulti && isOffered && (
              <CheckIcon
                aria-hidden="true"
                className={classList({
                  'tw:size-4 tw:shrink-0': true,
                  'tw:invisible': !isSelected,
                })}
              />
            )}
          </div>
          {note && (
            <div
              style={{
                paddingLeft:
                  ROW_INSET_PX +
                  checkboxSlotPx +
                  depth * GROUP_INDENT_STEP_PX +
                  CHEVRON_SLOT_PX +
                  ICON_SLOT_PX,
              }}
            >
              <WarningNote note={note} className="tw:-mt-1 tw:pr-3 tw:pb-2" />
            </div>
          )}
        </div>
      </div>
    )
  },
)

interface IProps {
  countLabel?: string
  portalSelector?: string
}

const PickerTree: React.FC<IProps> = ({ countLabel, portalSelector }) => {
  const {
    sortedRecords,
    unfilteredRecords,
    pickableIds,
    selectedIds,
    mode,
    isSearchApplied: isSearching,
    loading,
  } = useResourcePickerContext()

  const tree = useMemo(
    () =>
      buildRecordTree(sortedRecords, unfilteredRecords, id =>
        pickableIds.has(id),
      ),
    [sortedRecords, unfilteredRecords, pickableIds],
  )

  // Bands keyed by the records that have children: the collapse state, the descendant
  // counts and the summary bar's level controls, while the rows are drawn below.
  const groupConfig = useMemo(
    (): ICardListGroupConfig<IResourceRecord> => ({
      groupBy: record => {
        const parentId = tree.getParentId(record.id)
        return parentId ? [parentId] : []
      },
      getGroupLabel: key => tree.recordById.get(key)?.name ?? '',
      getGroupAncestors: tree.getAncestorIds,
      sortGroups: () => 0,
    }),
    [tree],
  )
  const listState = useCardListCore({ items: sortedRecords, groupConfig })
  const { collapsedKeys, toggleGroup: toggleExpanded } = listState

  const countById = useMemo(
    () => new Map(listState.groups.map(group => [group.key, group.count])),
    [listState.groups],
  )

  const rows = useMemo(() => {
    const result: ITreeRow[] = []
    const visit = (id: string, depth: number) => {
      const childIds = tree.childIdsById.get(id) ?? []
      const hasChildren = childIds.length > 0
      const isExpanded = hasChildren && (isSearching || !collapsedKeys.has(id))
      result.push({
        id,
        record: tree.recordById.get(id)!,
        depth,
        hasChildren,
        isExpanded,
        count: countById.get(id),
      })
      if (isExpanded) childIds.forEach(childId => visit(childId, depth + 1))
    }
    tree.rootIds.forEach(id => visit(id, 0))
    return result
  }, [tree, isSearching, collapsedKeys, countById])

  const firstSelectedId = useFirstInitiallySelectedId(rows, selectedIds)

  const renderRow = React.useCallback(
    (row: ITreeRow) => (
      <PickerTreeRow
        row={row}
        tree={tree}
        isSearching={isSearching}
        onToggleExpanded={toggleExpanded}
      />
    ),
    [tree, isSearching, toggleExpanded],
  )

  if (loading) return <PickerLoading />

  if (sortedRecords.length === 0) {
    return (
      <PickerCountRow
        items={sortedRecords}
        countLabel={countLabel}
        portalSelector={portalSelector}
      />
    )
  }

  return (
    <CardList
      role="tree"
      ariaMultiselectable={mode === 'multi'}
      items={rows}
      initialScrollToItemId={firstSelectedId}
      estimatedItemHeight={32}
      header={
        <PickerCountRow
          items={sortedRecords}
          listState={isSearching ? undefined : listState}
          countLabel={countLabel}
          portalSelector={portalSelector}
        />
      }
      renderItem={renderRow}
    />
  )
}

export default PickerTree
